import { DateTime } from "luxon";

import { APP_CONFIG } from "@/lib/config/app-config";

const MS_IN_DAY = 24 * 60 * 60 * 1000;

export const VIEW_TYPES = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
};

// Read the configured first day of the week (date-fns convention: 0=Sun..6=Sat)
// and translate it to Luxon's weekday numbering (1=Mon..7=Sun) for internal math.
export const WEEK_STARTS_ON = APP_CONFIG.WEEK_STARTS_ON ?? 1;
const LUXON_WEEK_START = WEEK_STARTS_ON === 0 ? 7 : WEEK_STARTS_ON;

// Weekday header labels rotated to start on the configured first day of the week.
const WEEKDAY_LABELS_BASE = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAY_LABELS_TWO = WEEKDAY_LABELS_BASE.map((name) => name.slice(0, 2));
export const WEEKDAY_LABELS_SHORT = [
  ...WEEKDAY_LABELS_BASE.slice(LUXON_WEEK_START - 1),
  ...WEEKDAY_LABELS_BASE.slice(0, LUXON_WEEK_START - 1),
];
// Two-letter headers (Sa/Su vs a single "S") for compact calendar grids.
export const WEEKDAY_HEADERS_TWO = [
  ...WEEKDAY_LABELS_TWO.slice(LUXON_WEEK_START - 1),
  ...WEEKDAY_LABELS_TWO.slice(0, LUXON_WEEK_START - 1),
];

/** Unambiguous weekday abbreviation for a date (Mo, Tu, …, Sa, Su). */
export function formatWeekdayHeader(date) {
  return WEEKDAY_LABELS_TWO[toDateTime(date).weekday - 1] ?? "";
}

/** Placeholder patient id for appointments not yet linked to a patient record. */
export const NIL_PATIENT_ID = "000000000000000000000000";

/**
 * "New patient" = no Plato record yet. The server decides (item.newPatient:
 * zero-id bookings, unresolvable refs, local-only patients — but not
 * patient-less blockers); the zero-id fallback covers locally built objects
 * (drag ghosts, optimistic bookings) and stale payloads.
 * @param {{ newPatient?: boolean, patientId?: string }} [event]
 */
export function isNewPatient(event) {
  if (typeof event?.newPatient === "boolean") {
    return event.newPatient;
  }
  return event?.patientId === NIL_PATIENT_ID;
}

/**
 * The phone number a new-patient booking carries. These bookings have no user
 * record, so the phone lives on the appointment metadata:
 * sourceAppointmentSnapshot.handphone (raw, as typed) with handphoneNormalized
 * (digits only) as a fallback. Empty string when the booking has no phone —
 * such appointments can't be messaged. Enables chat/confirmation from the number
 * alone (the backend find-or-creates a patient by phone).
 * @param {{ metadata?: any, _patient?: { phone?: string } }} [event]
 */
export function newPatientPhoneFromEvent(event) {
  const raw =
    event?.metadata?.sourceAppointmentSnapshot?.handphone ||
    event?.metadata?.handphoneNormalized ||
    event?._patient?.phone ||
    "";
  return String(raw).trim();
}

/** @param {unknown} status */
export function isCancelledCalendarStatus(status) {
  const s = String(status || "").trim().toLowerCase();
  return s === "cancelled" || s === "canceled";
}

/** @param {{ status?: string }} [event] */
export function isCancelledCalendarEvent(event) {
  return isCancelledCalendarStatus(event?.status);
}

// --- Lock-first-15-minutes (metadata.lock15m) -------------------------------
// A flagged appointment still DISPLAYS its full duration, but only its first
// 15 minutes block slot availability (server side), so the remainder can be
// booked. An explicit event.lock15m boolean (mapped from metadata.lock15m, or
// an optimistic change riding on the event) wins; absent defaults to ON for
// exactly-30-minute appointments. Mirror of the Go helper
// storage.AppointmentLock15m — keep the two in sync.
export const LOCK_15M_MINUTES = 15;

export function effectiveLock15m(event) {
  if (!event || event.kind !== "appointment") return false;
  const explicit =
    typeof event.lock15m === "boolean"
      ? event.lock15m
      : typeof event.metadata?.lock15m === "boolean"
        ? event.metadata.lock15m
        : undefined;
  if (typeof explicit === "boolean") return explicit;
  if (!event.startAt || !event.endAt) return false;
  // EXACTLY 30 minutes, no rounding — must agree with the Go mirror
  // (storage.AppointmentLock15m), which owns slot blocking.
  return event.endAt.diff(event.startAt, "minutes").minutes === 30;
}

/**
 * True when the lock actually frees part of the appointment — i.e. it is in
 * effect AND the appointment is longer than the 15-minute locked window (so
 * there is a bookable remainder to mark on the card).
 */
export function isAppointmentPartiallyFree(event) {
  if (!effectiveLock15m(event) || !event?.startAt || !event?.endAt) return false;
  return event.endAt.diff(event.startAt, "minutes").minutes > LOCK_15M_MINUTES;
}

export function toDateTime(value = DateTime.now()) {
  if (DateTime.isDateTime(value)) {
    return value;
  }

  if (value instanceof Date) {
    return DateTime.fromJSDate(value);
  }

  if (typeof value === "string") {
    return DateTime.fromISO(value);
  }

  if (typeof value === "number") {
    return DateTime.fromMillis(value);
  }

  return DateTime.now();
}

export function startOfDay(value) {
  return toDateTime(value).startOf("day");
}

export function endOfDay(value) {
  return toDateTime(value).endOf("day");
}

export function addDays(value, days) {
  return toDateTime(value).plus({ days });
}

export function addMonths(value, months) {
  return toDateTime(value).plus({ months });
}

export function startOfWeek(value) {
  // Luxon's `startOf("week")` is hardcoded to ISO Monday; roll back to the
  // configured first day instead so date pickers and grids stay aligned.
  const dt = toDateTime(value).startOf("day");
  const offset = (dt.weekday - LUXON_WEEK_START + 7) % 7;
  return dt.minus({ days: offset });
}

export function endOfWeek(value) {
  return startOfWeek(value).plus({ days: 6 }).endOf("day");
}

export function startOfMonth(value) {
  return toDateTime(value).startOf("month");
}

export function endOfMonth(value) {
  return toDateTime(value).endOf("month");
}

export function isSameDay(left, right) {
  return toDateKey(left) === toDateKey(right);
}

export function toDateKey(value) {
  return startOfDay(value).toFormat("yyyy-LL-dd");
}

export function formatMonthYear(value) {
  return toDateTime(value).toFormat("LLLL yyyy");
}

export function formatDayHeading(value) {
  return toDateTime(value).toFormat("cccc, LLLL d, yyyy");
}

export function formatWeekRange(value) {
  const first = startOfWeek(value);
  const last = addDays(first, 6);

  const firstMonth = first.month;
  const lastMonth = last.month;
  const firstYear = first.year;
  const lastYear = last.year;

  if (firstYear === lastYear && firstMonth === lastMonth) {
    return `${first.toFormat("LLLL")} ${first.day}-${last.day}, ${firstYear}`;
  }

  const firstPart = first.toFormat("LLL d");
  const lastPart = last.toFormat("LLL d");
  return `${firstPart} - ${lastPart}, ${lastYear}`;
}

export function formatTime(value, options = {}) {
  const { hour12 = true, includeMinutes = true } = options;
  const dateTime = toDateTime(value);
  const pattern = hour12
    ? includeMinutes
      ? "h:mm a"
      : "h a"
    : includeMinutes
      ? "HH:mm"
      : "HH";
  return dateTime.toFormat(pattern);
}

export function formatHourLabel(hour, options = {}) {
  const { hour12 = false } = options;
  const safeHour = ((hour % 24) + 24) % 24;
  const labelDate = DateTime.fromObject({ year: 2024, month: 1, day: 1, hour: safeHour, minute: 0 });
  // These labels only ever land on the hour (the caller gates rendering on `tick.isHour`), so
  // the ":00" minutes are always redundant. Omitting them ("9 AM" / "10 AM" instead of
  // "9:00 AM" / "10:00 AM") keeps every label on one line in the narrow gutter column —
  // otherwise double-digit hours wrap to a second line while single-digit ones don't,
  // producing an inconsistent stagger down the axis.
  return formatTime(labelDate, { hour12, includeMinutes: false });
}

/**
 * A sub-hour gutter label ("9:30" / "09:30") from minutes-into-the-day.
 *
 * Deliberately NOT formatHourLabel: that one takes a whole hour, and a :30 row's
 * `hour` is a fraction (9.5), which DateTime.fromObject rejects - it would render
 * the literal string "Invalid DateTime".
 *
 * The meridiem is dropped on purpose even in 12-hour mode. The gutter is w-14
 * (56px) less pr-2, so ~48px of nowrap text, and "12:45 AM" overruns it - the same
 * width budget that made formatHourLabel drop ":00" above. The hour label directly
 * overhead already carries AM/PM, so "12:45" under "12 AM" is unambiguous.
 */
export function formatSubHourLabel(minutesOfDay, options = {}) {
  const { hour12 = false } = options;
  const total = ((Math.round(Number(minutesOfDay) || 0) % 1440) + 1440) % 1440;
  const labelDate = DateTime.fromObject({
    year: 2024,
    month: 1,
    day: 1,
    hour: Math.floor(total / 60),
    minute: total % 60,
  });
  return labelDate.toFormat(hour12 ? "h:mm" : "HH:mm");
}

export function getWeekDays(anchorDate, { hideSunday = false } = {}) {
  const firstDay = startOfWeek(anchorDate);
  const days = Array.from({ length: 7 }, (_, index) => addDays(firstDay, index));
  return hideSunday ? days.filter((day) => day.weekday !== 7) : days;
}

export function getMonthMatrix(anchorDate, { hideSunday = false } = {}) {
  const monthStart = startOfMonth(anchorDate);
  const gridStart = startOfWeek(monthStart);
  const currentMonth = monthStart.month;

  const cells = Array.from({ length: 42 }, (_, index) => {
    const date = addDays(gridStart, index);
    return {
      date,
      isCurrentMonth: date.month === currentMonth,
    };
  });
  // Dropping each week's Sunday leaves a clean 6×6 grid (36 cells).
  return hideSunday ? cells.filter((cell) => cell.date.weekday !== 7) : cells;
}

// The configured weekday columns in display order, as ISO weekdays (1=Mon…7=Sun).
export const WEEKDAY_ORDER = Array.from(
  { length: 7 },
  (_, idx) => ((LUXON_WEEK_START - 1 + idx) % 7) + 1,
);

// Two-letter weekday headers in display order, honoring the hide-Sunday option.
// Pairs with getWeekDays/getMonthMatrix so headers and cells stay aligned.
export function getWeekdayHeadersTwo({ hideSunday = false } = {}) {
  return WEEKDAY_ORDER.filter((iso) => !hideSunday || iso !== 7).map(
    (iso) => WEEKDAY_LABELS_TWO[iso - 1],
  );
}

// Tailwind grid-template-columns class for a weekday grid (6 cols when Sunday
// is hidden, otherwise 7). Static strings so Tailwind keeps them.
export function weekdayGridColsClass(hideSunday = false) {
  return hideSunday ? "grid-cols-6" : "grid-cols-7";
}

export function groupEventsByDate(events) {
  return events.reduce((accumulator, event) => {
    const key = toDateKey(event.startAt);
    if (!accumulator[key]) {
      accumulator[key] = [];
    }
    accumulator[key].push(event);
    return accumulator;
  }, {});
}

// Lane memory for the overlap layout below: appointments keep the lane they
// last rendered in whenever it is still free, so moving/resizing ONE
// appointment doesn't reshuffle every overlapping neighbour into new lanes
// (and the moved card itself stays in its accustomed lane). Slots are exempt —
// they are pinned leftmost for cross-day alignment and never move. Bounded so
// long sessions across many weeks can't grow it unchecked.
const appointmentLaneMemory = new Map();
const LANE_MEMORY_MAX_ENTRIES = 4000;

export function computeEventColumnLayout(events) {
  const sortedEvents = [...events].sort((left, right) => {
    const startDiff = left.startAt.toMillis() - right.startAt.toMillis();
    if (startDiff !== 0) {
      return startDiff;
    }
    return left.endAt.toMillis() - right.endAt.toMillis();
  });

  const layoutByEventId = {};
  let clusterEvents = [];
  let clusterMaxEnd = Number.NEGATIVE_INFINITY;

  const flushCluster = () => {
    if (clusterEvents.length === 0) {
      return;
    }

    const columnEndTimes = [];
    const assignments = [];

    // Open slots take the leftmost columns so they line up consistently across
    // the day, with booked appointments filling the columns to their right.
    // Within each group we keep the incoming start-time order so the greedy
    // column coloring stays valid (no overlaps share a column).
    const orderedForColumns = [
      ...clusterEvents.filter((event) => event.kind === "slot"),
      ...clusterEvents.filter((event) => event.kind !== "slot"),
    ];

    orderedForColumns.forEach((event) => {
      const eventStart = event.startAt.toMillis();
      const eventEnd = event.endAt.toMillis();
      let columnIndex = -1;

      // Sticky lane: reuse the appointment's previous lane when free.
      if (event.kind !== "slot") {
        const remembered = appointmentLaneMemory.get(event.id);
        if (
          remembered != null &&
          (remembered >= columnEndTimes.length || columnEndTimes[remembered] <= eventStart)
        ) {
          while (columnEndTimes.length <= remembered) {
            columnEndTimes.push(Number.NEGATIVE_INFINITY);
          }
          columnEndTimes[remembered] = eventEnd;
          columnIndex = remembered;
        }
      }

      if (columnIndex === -1) {
        columnIndex = columnEndTimes.findIndex((columnEndTime) => columnEndTime <= eventStart);
        if (columnIndex === -1) {
          columnIndex = columnEndTimes.length;
          columnEndTimes.push(eventEnd);
        } else {
          columnEndTimes[columnIndex] = eventEnd;
        }
      }

      assignments.push({ id: event.id, kind: event.kind, columnIndex });
    });

    // Sticky lanes can leave holes (a remembered lane whose left neighbours
    // emptied out) — compact to dense indices, preserving relative order, so
    // clusters never render an empty stripe.
    const usedLanes = [...new Set(assignments.map((a) => a.columnIndex))].sort(
      (left, right) => left - right,
    );
    const laneRemap = new Map(usedLanes.map((lane, index) => [lane, index]));
    const totalColumns = Math.max(usedLanes.length, 1);
    assignments.forEach(({ id, kind, columnIndex }) => {
      const finalLane = laneRemap.get(columnIndex);
      layoutByEventId[id] = { columnIndex: finalLane, totalColumns };
      if (kind !== "slot") {
        if (appointmentLaneMemory.size >= LANE_MEMORY_MAX_ENTRIES) {
          appointmentLaneMemory.clear();
        }
        appointmentLaneMemory.set(id, finalLane);
      }
    });

    clusterEvents = [];
    clusterMaxEnd = Number.NEGATIVE_INFINITY;
  };

  sortedEvents.forEach((event) => {
    const eventStart = event.startAt.toMillis();
    const eventEnd = event.endAt.toMillis();

    if (clusterEvents.length === 0) {
      clusterEvents.push(event);
      clusterMaxEnd = eventEnd;
      return;
    }

    if (eventStart < clusterMaxEnd) {
      clusterEvents.push(event);
      clusterMaxEnd = Math.max(clusterMaxEnd, eventEnd);
      return;
    }

    flushCluster();
    clusterEvents.push(event);
    clusterMaxEnd = eventEnd;
  });

  flushCluster();
  return layoutByEventId;
}

// Mirrors the server-side Plato color set: either a CSS color name or a base64-encoded
// 3-byte RGB string. Keep this map in sync with healo-go `platoColorToHex`.
const PLATO_WEB_COLORS = {
  black: "#000000",
  white: "#FFFFFF",
  red: "#FF0000",
  lime: "#00FF00",
  blue: "#0000FF",
  yellow: "#FFFF00",
  cyan: "#00FFFF",
  aqua: "#00FFFF",
  magenta: "#FF00FF",
  fuchsia: "#FF00FF",
  silver: "#C0C0C0",
  gray: "#808080",
  grey: "#808080",
  maroon: "#800000",
  olive: "#808000",
  green: "#008000",
  purple: "#800080",
  teal: "#008080",
  navy: "#000080",
  orange: "#FFA500",
  pink: "#FFC0CB",
  brown: "#A52A2A",
  gold: "#FFD700",
  violet: "#EE82EE",
  indigo: "#4B0082",
  turquoise: "#40E0D0",
};

/**
 * Convert a Plato `sourceColor` (CSS color name or base64-encoded 3-byte RGB)
 * into a `#RRGGBB` string. Returns "" when the value isn't a recognizable Plato
 * color so callers can fall through to the next color source.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function platoColorToHex(value) {
  const input = typeof value === "string" ? value.trim() : "";
  if (!input) return "";

  const named = PLATO_WEB_COLORS[input.toLowerCase()];
  if (named) return named;

  try {
    const decoded = atob(input);
    if (decoded.length !== 3) return "";
    const hex = [0, 1, 2]
      .map((i) => decoded.charCodeAt(i).toString(16).padStart(2, "0").toUpperCase())
      .join("");
    return `#${hex}`;
  } catch {
    return "";
  }
}

function hexToRgb(value) {
  const input = typeof value === "string" ? value.trim() : "";
  if (!input) return null;
  const normalized = input.startsWith("#") ? input.slice(1) : input;

  if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
    const expanded = normalized
      .split("")
      .map((char) => `${char}${char}`)
      .join("");
    return {
      r: Number.parseInt(expanded.slice(0, 2), 16),
      g: Number.parseInt(expanded.slice(2, 4), 16),
      b: Number.parseInt(expanded.slice(4, 6), 16),
    };
  }

  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }

  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
}

function mixRgb(base, target, weight) {
  return {
    r: base.r * (1 - weight) + target.r * weight,
    g: base.g * (1 - weight) + target.g * weight,
    b: base.b * (1 - weight) + target.b * weight,
  };
}

function rgbToHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) {
    return { h: 0, s: 0, l };
  }
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h: h * 360, s, l };
}

function hslToRgb({ h, s, l }) {
  const hn = (((h % 360) + 360) % 360) / 360;
  if (s === 0) {
    return { r: l * 255, g: l * 255, b: l * 255 };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t) => {
    let tn = t;
    if (tn < 0) tn += 1;
    if (tn > 1) tn -= 1;
    if (tn < 1 / 6) return p + (q - p) * 6 * tn;
    if (tn < 1 / 2) return q;
    if (tn < 2 / 3) return p + (q - p) * (2 / 3 - tn) * 6;
    return p;
  };
  return {
    r: channel(hn + 1 / 3) * 255,
    g: channel(hn) * 255,
    b: channel(hn - 1 / 3) * 255,
  };
}

// Fallback base for the per-appointment color picker when the appointment has
// no calendar color to riff on — matches the default emerald card theme.
export const DEFAULT_EVENT_ACCENT_HEX = "#10B981";

/**
 * Build the per-appointment color choices: ten complementary colors spread
 * evenly around the color wheel (base hue + 36° steps) at one shared, pleasant
 * saturation/lightness — genuinely different colors that sit well next to
 * each other, not shades of the base.
 *
 * @param {string} [baseHex]
 * @returns {string[]} distinct "#RRGGBB" values, base hue first
 */
export function buildEventColorChoices(baseHex) {
  const rgb = hexToRgb(baseHex) || hexToRgb(DEFAULT_EVENT_ACCENT_HEX);
  const { h, s } = rgbToHsl(rgb);
  // A near-gray base has no meaningful hue to anchor on — start the wheel at
  // the emerald default's hue instead.
  const hue = s < 0.08 ? rgbToHsl(hexToRgb(DEFAULT_EVENT_ACCENT_HEX)).h : h;

  const seen = new Set();
  const colors = [];
  for (let step = 0; step < 10; step += 1) {
    const hex = rgbToHex(hslToRgb({ h: hue + step * 36, s: 0.68, l: 0.55 }));
    if (seen.has(hex)) continue;
    seen.add(hex);
    colors.push(hex);
  }
  return colors;
}

/**
 * True when the "YYYY-MM-DD" date of birth lands on the given date's calendar
 * day — the patient's birthday.
 * @param {string} [dobIso]
 * @param {import("luxon").DateTime} [date]
 */
export function isBirthdayOnDate(dobIso, date) {
  if (!dobIso || !date) return false;
  const dob = DateTime.fromISO(String(dobIso));
  if (!dob.isValid) return false;
  return dob.month === date.month && dob.day === date.day;
}

/** Age turned on the birthday occurring at `date` (null when not computable). */
export function birthdayAgeOnDate(dobIso, date) {
  if (!dobIso || !date) return null;
  const dob = DateTime.fromISO(String(dobIso));
  if (!dob.isValid) return null;
  const age = date.year - dob.year;
  return age > 0 && age < 130 ? age : null;
}

export const EVENT_COLOR_CLASSES = {
  blue: "bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-600/15 dark:text-blue-300 dark:hover:bg-blue-600/20",
  indigo:
    "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-600/15 dark:text-indigo-300 dark:hover:bg-indigo-600/20",
  emerald:
    "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-600/15 dark:text-emerald-300 dark:hover:bg-emerald-600/20",
  purple:
    "bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-600/15 dark:text-purple-300 dark:hover:bg-purple-600/20",
  pink: "bg-pink-50 text-pink-700 hover:bg-pink-100 dark:bg-pink-600/15 dark:text-pink-300 dark:hover:bg-pink-600/20",
  orange:
    "bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-600/15 dark:text-orange-300 dark:hover:bg-orange-600/20",
  cyan: "bg-cyan-50 text-cyan-700 hover:bg-cyan-100 dark:bg-cyan-600/15 dark:text-cyan-300 dark:hover:bg-cyan-600/20",
  rose: "bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-600/15 dark:text-rose-300 dark:hover:bg-rose-600/20",
  amber:
    "bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-600/15 dark:text-amber-300 dark:hover:bg-amber-600/20",
};

// Creates a light + dark palette from one hex color that keeps event blocks readable.
export function buildEventColorTheme(colorHex) {
  const raw = hexToRgb(colorHex);
  if (!raw) {
    return null;
  }

  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };

  // Pastel base: soften the input color first so all theme variants stay pastel.
  const base = mixRgb(raw, white, 0.52);

  // Mimics Tailwind-like palette intent:
  // light: bg-50 / hover:bg-100 / text-700
  // dark:  bg-600/15 / hover:bg-600/20 / text-300
  const lightBackground = rgbToHex(mixRgb(base, white, 0.9));
  const lightBackgroundHover = rgbToHex(mixRgb(base, white, 0.84));
  const lightBorder = rgbToHex(mixRgb(base, white, 0.7));
  const lightText = rgbToHex(mixRgb(base, black, 0.5));

  // Dark mode: push toward near-black so tinted slots don't read as neon on dark UI; hover slightly lifts.
  const darkBackground = rgbToHex(mixRgb(base, black, 0.62));
  const darkBackgroundHover = rgbToHex(mixRgb(base, black, 0.54));
  const darkBorder = rgbToHex(mixRgb(base, black, 0.48));
  const darkText = rgbToHex(mixRgb(base, white, 0.66));

  // const dotLight = rgbToHex(mixRgb(base, black, 0.08));
  const dotLight = rgbToHex(mixRgb(base, white, 0.2));
  const dotDark = rgbToHex(mixRgb(base, black, 0.55));

  // Ghost fills for slots — much lighter than the filled-booking background so that
  // slot cards read as "available placeholders" even when tinted by the calendar color.
  // Light: ~4% color; Dark: pushed near black so it's still distinguishable from canvas.
  const ghostBgLight = rgbToHex(mixRgb(base, white, 0.96));
  const ghostBgHoverLight = rgbToHex(mixRgb(base, white, 0.92));
  const ghostBgDark = rgbToHex(mixRgb(base, black, 0.82));
  const ghostBgHoverDark = rgbToHex(mixRgb(base, black, 0.76));

  return {
    "--event-bg-light": lightBackground,
    "--event-bg-hover-light": lightBackgroundHover,
    "--event-border-light": lightBorder,
    "--event-text-light": lightText,
    "--event-bg-dark": darkBackground,
    "--event-bg-hover-dark": darkBackgroundHover,
    "--event-border-dark": darkBorder,
    "--event-text-dark": darkText,
    "--event-dot-light": dotLight,
    "--event-dot-dark": dotDark,
    "--event-bg-ghost-light": ghostBgLight,
    "--event-bg-ghost-hover-light": ghostBgHoverLight,
    "--event-bg-ghost-dark": ghostBgDark,
    "--event-bg-ghost-hover-dark": ghostBgHoverDark,
  };
}

export function sortEventsByStart(events) {
  return [...events].sort((left, right) => left.startAt.toMillis() - right.startAt.toMillis());
}

export function getHourRows() {
  return Array.from({ length: 24 }, (_, hour) => {
    const labelDate = DateTime.fromObject({ year: 2024, month: 1, day: 1, hour, minute: 0 });
    return {
      hour,
      label: labelDate.toFormat("h a"),
    };
  });
}

export function eventPosition(event) {
  const start = toDateTime(event.startAt);
  const end = toDateTime(event.endAt);
  const startMinutes = start.hour * 60 + start.minute;
  const endMinutes = end.hour * 60 + end.minute;
  return {
    topPercent: (startMinutes / (24 * 60)) * 100,
    heightPercent: (Math.max(endMinutes - startMinutes, 30) / (24 * 60)) * 100,
  };
}

export function isToday(value) {
  return isSameDay(value, DateTime.now());
}

export function rangeFromView(anchorDate, view) {
  if (view === VIEW_TYPES.DAY) {
    return { start: startOfDay(anchorDate), end: endOfDay(anchorDate) };
  }

  if (view === VIEW_TYPES.WEEK) {
    return { start: startOfWeek(anchorDate), end: endOfWeek(anchorDate) };
  }

  return { start: startOfMonth(anchorDate), end: endOfMonth(anchorDate) };
}

/**
 * Horizontal footprint of the DAY view's time grid, as an inline style.
 *
 *   > 100% → `min-width` pushes the grid PAST its pane so it scrolls sideways.
 *   = 100% → nothing; the grid fills the pane.
 *   < 100% → an explicit `width` pulls it in, so the columns render narrower
 *            than the pane and sit flush against its left edge.
 *
 * The WEEK view does not use this: its columns are days, so instead of leaving
 * the surplus blank it fills the width with neighbouring days — see
 * week-day-window.js.
 *
 * Returns null when no style is needed, so callers can pass it straight to
 * `style` (React ignores null/undefined).
 */
export function timeGridWidthStyle(widthZoomPercent) {
  const factor = Math.max(Number(widthZoomPercent) || 100, 0) / 100;
  if (factor > 1) return { minWidth: `${100 * factor}%` };
  if (factor >= 1) return null;
  return { width: `${100 * factor}%` };
}

/**
 * Drop open slots from a dateKey → events map, keeping only real bookings.
 *
 * Used by the month grid: a clinic generates dozens of slots a day and books a
 * handful, so slots would fill each cell's two visible rows and push the actual
 * appointments into "+ N more". Days left with nothing are omitted entirely, so
 * callers can treat a missing key and an empty day the same way.
 */
export function bookingsByDateOnly(eventsByDate) {
  const byDate = {};
  for (const [dayKey, dayEvents] of Object.entries(eventsByDate || {})) {
    const bookings = (dayEvents || []).filter((event) => event?.kind !== "slot");
    if (bookings.length > 0) byDate[dayKey] = bookings;
  }
  return byDate;
}

export { MS_IN_DAY };
